import { Autocomplete, Box, Button, Card, MenuItem, Modal, Select, TextField, useTheme } from '@mui/material';
import { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CaseResponseDto } from '@bandido/shared/dtos';
import { CaseRelationType, OriginatedByChannel } from '@bandido/shared/types';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { client } from '@frontend/features/common/services';
import { defaultLeadingZeros } from '@frontend/features/common/constants';
import { addLeadingZeros } from '@frontend/features/common/utils/helpers';

import { useGetAllCases } from '../../queries';
import { useNavigate } from 'react-router-dom';
import { routes } from '@frontend/router/routes';

enum CaseLinkEnum {
  LINK = 'LINK',
  DUPLICATES = 'DUPLICATES',
  NEW_CASE = 'NEW_CASE',
}

const linkOptions = [
  {
    id: CaseLinkEnum.NEW_CASE,
    name: 'linkCaseModal.newCase',
  },
  {
    id: CaseLinkEnum.LINK,
    name: 'linkCaseModal.linkedTo',
  },
  {
    id: CaseLinkEnum.DUPLICATES,
    name: 'linkCaseModal.duplicates',
  },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  fromCaseId: string;
}

export const LinkCaseModal = ({ isOpen, onClose, fromCaseId }: Props) => {
  const { addToast } = useSnackBar();
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [newCaseName, setNewCaseName] = useState('');
  const [selectedLinkType, setSelectedLinkType] = useState<CaseLinkEnum>(CaseLinkEnum.NEW_CASE);
  const [autocompleteSearch, setAutoCompleteSearch] = useState('');
  const [autocompleteOptions, setAutocompleteOptions] = useState<CaseResponseDto[]>([]);
  const [selectedAutocompleteCase, setSelectedAutocompleteCase] = useState<CaseResponseDto | null>(null);

  const { data } = useGetAllCases({
    currentPage: 1,
    pageSize: 20,
    search: autocompleteSearch,
  });

  useEffect(() => {
    if (data?.items) {
      setAutocompleteOptions(data.items.filter((item) => item.id !== Number(fromCaseId)));
    }
  }, [data, setAutocompleteOptions]);

  const handleSubmitCreate = async () => {
    try {
      // crea a new case and link it
      if (selectedLinkType === CaseLinkEnum.NEW_CASE) {
        const url = `case/${fromCaseId}/create-and-link`;
        const result = await client.post(url, {
          name: newCaseName,
          originatedByChannel: OriginatedByChannel.MANUAL,
        });
        navigate(routes.dashboard.caseView.replace(':id', result.data.id));
        setNewCaseName('');
        onClose();
        addToast({
          message: t('linkCaseModal.linkSuccess'),
          color: 'success',
        });
      } else {
        // Is link or dupe
        const url = `case/${fromCaseId}/related`;
        if (selectedAutocompleteCase) {
          await client.post(url, {
            relatedCaseId: selectedAutocompleteCase.id,
            type: selectedLinkType === CaseLinkEnum.DUPLICATES ? CaseRelationType.DUPED : CaseRelationType.LINKED,
          });

          onClose();
          addToast({
            message: t('linkCaseModal.linkSuccess'),
            color: 'success',
          });
        } else {
          addToast({
            message: t('linkCaseModal.selectCaseError'),
            color: 'error',
          });
        }
      }
    } catch (_e) {
      addToast({
        message: t('linkCaseModal.linkError'),
        color: 'error',
      });
    }
  };

  const handleNewCaseChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement | null>) => {
    setNewCaseName(event.target.value);
  };

  const handleAutocompleteSearchChange = (_event: SyntheticEvent<Element, Event>, value: string) => {
    setAutoCompleteSearch(value);
  };

  const handleChange = (_event: SyntheticEvent<Element, Event>, value: CaseResponseDto | null) => {
    setSelectedAutocompleteCase(value);
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Card
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          maxWidth: 800,
          p: 2,
        }}
        elevation={4}
      >
        <Box component="form" display="flex" gap={2}>
          <Box sx={{ width: 250 }}>
            <Select
              labelId="linkType-selector"
              id="linkType-selector"
              value={selectedLinkType}
              onChange={(e) => setSelectedLinkType(e.target.value as unknown as CaseLinkEnum)}
              fullWidth
            >
              {linkOptions?.map((value) => (
                <MenuItem key={value.id} value={value.id}>
                  {t(value.name)}
                </MenuItem>
              ))}
            </Select>
          </Box>
          {selectedLinkType === CaseLinkEnum.NEW_CASE ? (
            <TextField
              autoFocus
              size="medium"
              placeholder={t('cases.create.placeholder')}
              value={newCaseName}
              onChange={handleNewCaseChange}
              fullWidth
            />
          ) : (
            <Autocomplete
              inputValue={autocompleteSearch}
              onInputChange={handleAutocompleteSearchChange}
              options={autocompleteOptions}
              getOptionLabel={(option) =>
                `${t('cases.view.case')} #: ${addLeadingZeros(option.id, defaultLeadingZeros)} - ${option.name}`
              }
              renderInput={(params) => <TextField {...params} label={t('linkCaseModal.searchTextLabel')} />}
              onChange={handleChange}
              fullWidth
            />
          )}

          <Button
            variant="contained"
            color="primary"
            sx={{ padding: `0 ${theme.spacing(3)}` }}
            onClick={handleSubmitCreate}
          >
            {t('common.confirm')}
          </Button>
        </Box>
      </Card>
    </Modal>
  );
};
